import { useEffect, useRef, useState } from 'react';

export default function BusinessSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="relative w-full min-h-screen overflow-hidden">
      {/* 背景画像 */}
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/0d0bbe412fe145bb93ddf84db95bab31.jpeg"
          alt="ビジネスシーンでAtelier Folioを使用する様子"
          className="w-full h-full object-cover object-center"
        />
      </div>

      {/* グラデーションオーバーレイ */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent"></div>

      {/* コンテンツ */}
      <div className="relative z-10 h-full min-h-screen flex items-center justify-start">
        <div className="max-w-7xl mx-auto w-full px-8 lg:px-16 py-24">
          <div className={`max-w-lg mr-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            {/* ラベル */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-white/10 backdrop-blur-sm rounded-full mb-6">
              <i className="ri-briefcase-line text-white/70 text-sm"></i>
              <span className="text-xs font-medium text-white/70 tracking-wider">For Business</span>
            </div>

            {/* メインキャッチコピー */}
            <h2
              className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-[1.2] tracking-tight"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              クリエイターだけの
              <br />
              ものじゃない。
            </h2>

            {/* サブキャッチ */}
            <p className="text-xl lg:text-2xl text-white/90 font-light mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              ビジネスシーンでも、<span className="text-[#D4B8E8]">静かに活躍</span>。
            </p>

            {/* 説明文 */}
            <div className="space-y-3 mb-8">
              <p className="text-sm lg:text-base text-white/60 leading-relaxed">
                会議でのメモ取り、プレゼン資料の確認、外出先でのメール対応。
              </p>
              <p className="text-sm lg:text-base text-white/60 leading-relaxed">
                洗練されたデザインと安定した角度が、ビジネスの現場でも頼れるパートナーに。
              </p>
            </div>

            {/* 特徴リスト */}
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center gap-3 px-4 py-3 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
                <div className="w-9 h-9 flex items-center justify-center bg-[#8B6BAE]/20 rounded-lg">
                  <i className="ri-keyboard-line text-lg text-white"></i>
                </div>
                <div>
                  <p className="text-white font-medium text-sm">キーボードとの相性</p>
                  <p className="text-xs text-white/50">最適な角度でタイピングも快適</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 px-4 py-3 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
                <div className="w-9 h-9 flex items-center justify-center bg-[#8B6BAE]/20 rounded-lg">
                  <i className="ri-video-chat-line text-lg text-white"></i>
                </div>
                <div>
                  <p className="text-white font-medium text-sm">オンライン会議に</p>
                  <p className="text-xs text-white/50">安定したスタンドで画面共有も楽々</p>
                </div>
              </div>

              <div className="flex items-center gap-3 px-4 py-3 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
                <div className="w-9 h-9 flex items-center justify-center bg-[#8B6BAE]/20 rounded-lg">
                  <i className="ri-suitcase-line text-lg text-white"></i>
                </div>
                <div>
                  <p className="text-white font-medium text-sm">出張・外出先で</p>
                  <p className="text-xs text-white/50">軽量で持ち運びやすく、どこでもセットアップ</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
